function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { formatToTipTapJSON } from "sprout-lib/dist/parser";
import { useHistory } from "react-router-dom";
import { useFnRef } from "sprout-hooks";
import { useSnackbar } from "../Snackbar";
import { getEditorStateHelpers } from "./utils";
import useMentionAutocomplete from "./components/useMentionAutocomplete";
import createExtensions from "./createExtensions";
import useTipTapEditor from "./useTipTapEditor";
import InternalEditor from "./EditorInternal";
const EditorForwardedRef = /*#__PURE__*/ forwardRef(function Editor(props, ref) {
    const { placeholder , mode , onEnter , onEscape , onSave , itemId , initialState , readOnly , onChange , onFocus: onFocusProp , onBlur: onBlurProp , autoFocus , className , ensureWithinViewport =true ,  } = props;
    const handleMentionAutocomplete = process.env.STORYBOOK ? undefined : // eslint-disable-next-line react-hooks/rules-of-hooks
    useMentionAutocomplete({
        itemId
    });
    const history = useHistory();
    const showSnackbar = useSnackbar();
    const extensions = useMemo(()=>createExtensions({
            showSnackbar,
            placeholder,
            mode,
            onEnter,
            onEscape,
            onMentionAutocomplete: handleMentionAutocomplete,
            onSave,
            history
        }), [
        handleMentionAutocomplete,
        history,
        mode,
        onEnter,
        onEscape,
        onSave,
        placeholder,
        showSnackbar, 
    ]);
    const onChangeRef = useFnRef(onChange);
    const onUpdate = useCallback(({ editor  })=>{
        if (onChangeRef.current) onChangeRef.current(getEditorStateHelpers(editor));
    }, [
        onChangeRef
    ]);
    const onBlur = useCallback(({ editor , event  })=>{
        if (onBlurProp) onBlurProp(getEditorStateHelpers(editor), event);
    }, [
        onBlurProp
    ]);
    const onFocus = useCallback(({ editor  })=>{
        if (onFocusProp) onFocusProp(getEditorStateHelpers(editor));
    }, [
        onFocusProp
    ]);
    const initialStateRef = useRef(initialState ? typeof initialState === "object" ? initialState : formatToTipTapJSON(initialState).json : undefined);
    const editorInstance = useTipTapEditor(useMemo(()=>_objectSpread({
            content: initialStateRef.current,
            extensions,
            editable: !readOnly,
            onUpdate,
            onFocus,
            onBlur
        }, ensureWithinViewport && {
            editorProps: {
                scrollMargin: 190,
                scrollThreshold: 190
            }
        }), [
        ensureWithinViewport,
        extensions,
        onBlur,
        onFocus,
        onUpdate,
        readOnly
    ]));
    /**
   * Call `autoFocus` from commands instead of `autoFocus` from the
   * editor options.
   *
   * https://github.com/ueberdosis/tiptap/blob/5daa870b0906f0387fe07041681bc6f5b3774617/packages/core/src/Editor.ts#L101
   * `autoFocus` from the editor options is called in a `setTimeout`, which is
   * potentially causing iOS focus issues.
   */ useEffect(()=>{
        if (autoFocus) editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.commands.focus(autoFocus, {
            scrollIntoView: ensureWithinViewport
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useImperativeHandle(ref, ()=>_objectSpread({}, getEditorStateHelpers(editorInstance)), [
        editorInstance
    ]);
    return /*#__PURE__*/ _jsx(InternalEditor, {
        placeholder: placeholder,
        editor: editorInstance,
        extensions: extensions,
        className: className,
        initialState: initialStateRef.current
    });
});
export default EditorForwardedRef;
